import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import {
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Location, NgFor, NgIf } from '@angular/common';
import { Subscription, debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { OrderBy, OrderByDirection } from '../../../../shared/utils/constant';
import {
	AddServiceResponse,
	DragDropServices,
	UserServiceResponse
} from '../../../models/user-service';
import { OrganizationService } from '../../../services/organization.service';
import { UserCardService } from '../../../services/user-card.service';
import { OrganizationServiceResponse } from '../../../models/organization-service-info';
import { SlideButtonComponent } from '../../../../shared/components/slide-button/slide-button.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';

const PAGE = 1;
const LIMIT = 10_000;
const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'service.id';
const SORT_BY_USER = 'id';

const orgServiceFields = [
	'service.id',
	'service.labelTranslationId',
	'service.descriptionTransId',
	'service.manageParameter',
	'supplier.id',
	'supplier.name',
	'serviceParameters.id',
	'serviceParameters.value',
	'serviceParameters.parameter.id',
	'serviceParameters.parameter.paramName',
	'serviceParameters.parameter.defaultValue',
	'serviceParameters.parameter.description'
];
@Component({
	selector: 'copo-user-manage-services',
	templateUrl: './user-manage-services.component.html',
	animations: pageTransitions,
	styleUrl: './user-manage-services.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		CdkDropList,
		NgFor,
		CdkDrag,
		NgIf,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		SlideButtonComponent,
		LoadingSpinnerComponent
	]
})

export class UserManageServicesComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() togglePosition = true;
	@Output() readonly typeOfTab = new EventEmitter<string>();
	@Output() readonly togglePositionChange = new EventEmitter<boolean>();
	searchTextControl = new FormControl('');
	allServices: DragDropServices[] = [];
	addedServices: DragDropServices[] = [];
	userId?: string;
	orgId?: string;
	supplierId = '';
	userServiceFilter = '';
	orgServiceFilter = '';
	routeSub: Subscription = Subscription.EMPTY;
	userServices?: UserServiceResponse;
	addServiceResponse?: AddServiceResponse[];
	organizationServices?: OrganizationServiceResponse[];
	deleteServiceResponse?: string[];
	searchTextSubscription?: Subscription;
	userCardServiceSub: Subscription = Subscription.EMPTY;
	isLoading = false;

	constructor(
		private readonly userCardService: UserCardService,
		private readonly route: ActivatedRoute,
		private readonly orgService: OrganizationService,
		private readonly location: Location

	) {
		if (this.route.parent === null) {
			throw new Error('wrong url');
		}
		this.routeSub = this.route.parent.paramMap.subscribe(params => {
			const idString = params.get('id');
			if (idString === null) {
				return;
			}
			this.userId = idString;
			this.orgId = <string> this.route.snapshot.params['memberId'];
		});
	}

	ngOnInit() {
		this.getUserServices().catch((err: unknown) => {
			console.error(err);
		});
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			const searchText = <string | undefined>value;
			if (searchText === undefined) {
				return;
			}
			if (searchText === '' && this.organizationServices) {
				this.getDragDropForOrg(this.organizationServices);
			} else {
				this.allServices = this.allServices.filter(allSer =>
					allSer.id === searchText || allSer.name.toLowerCase().includes(searchText.toLowerCase()));
			}
		});
	}

	goBack() {
		this.location.back();
	}

	onSave() {
		if (this.userId && this.userServices && this.orgId && this.supplierId) {
			const filteredResponse =
				this.userServices.services.filter(o1 => !this.addedServices.some(o2 => o1.id === o2.id));
			if (filteredResponse.length > 0) {
				this.deleteServiceResponse = [];
				for (const fr of filteredResponse) {
					this.deleteServiceResponse.push(fr.id);
				}
			}
			this.addServiceResponse =
				this.addedServices.filter(o1 =>
					this.userServices && !this.userServices.services.some(o2 => o1.id === o2.id));
			if (this.addServiceResponse.length > 0) {
				this.userCardServiceSub = this.userCardService
					.addServicesToUser(
						this.userId,
						this.userServices.supplier
							? this.userServices.supplier.id : Number(this.supplierId),
						this.orgId,
						this.addServiceResponse
					)
					.subscribe(
						{
							next: () => {
								if (filteredResponse.length <= 0) {
									this.goBack();
								}
							},
							error: (err: unknown) => {
								console.error('Error from https post add services to user', err);
							}
						}
					);
			}
			if (this.deleteServiceResponse && this.deleteServiceResponse.length > 0) {
				this.userCardServiceSub = this.userCardService.deleteServicesFromUser(
					this.userId,
					this.userServices.supplier
						? this.userServices.supplier.id : Number(this.supplierId),
					this.orgId,
					this.deleteServiceResponse
				)
					.subscribe(
						{
							next: () => {
								this.goBack();
							},
							error: (err: unknown) => {
								console.error('Error from http delete services from user', err);
							}
						}
					);
			}
		}
	}

	ngOnDestroy() {
		this.userCardServiceSub.unsubscribe();
		this.routeSub.unsubscribe();
	}

	async getOrganizationServices() {
		if (this.orgId) {
			return firstValueFrom(this.orgService.getOrganizationServices(
				this.orgId,
				orgServiceFields,
				this.orgServiceFilter,
				PAGE,
				LIMIT,
				new OrderBy(<keyof OrganizationServiceResponse> SORT_BY, SORT_WAY),
				false
			));
		}
		return undefined;
	}

	getDragDropForOrg(orgResponse: OrganizationServiceResponse[]) {
		this.allServices = [];
		for (const service of orgResponse) {
			this.supplierId = service.supplier.id.toString();
			const dragDropService: DragDropServices = {
				id: service.service.id,
				name: service.service.labelTranslationId,
				description: service.service.descriptionTransId,
			};
			this.allServices.push(dragDropService);
		}
		this.sortAllSelectedService(this.allServices);
	}

	async getUserServices() {
		if (this.userId) {
			this.addedServices = [];
			try {
				this.isLoading = true;
				const [userServicesByOrganization, services] = await Promise.all([
					firstValueFrom(
						this.userCardService.getServicesForUserByOrganization(
							Number(this.userId),
							this.userServiceFilter,
							PAGE,
							LIMIT,
							new OrderBy(SORT_BY_USER, SORT_WAY),
							false
						)
					),
					this.getOrganizationServices()
				]);

				this.userServices = userServicesByOrganization.find(
					userServicesOrga => userServicesOrga.id === Number(this.orgId)
				);
				if (!this.userServices) {
					console.error('No organization found');
					return;
				}
				for (const service of this.userServices.services) {
					const dragDropService: DragDropServices = {
						id: service.id,
						name: service.name,
						description: service.description,
					};
					this.addedServices.push(dragDropService);
				}
				this.sortAllSelectedService(this.addedServices);
				if (!services) {
					return;
				}
				this.organizationServices = services.results;
				this.organizationServices = this.organizationServices.filter(o1 =>
					!this.userServices?.services.some(o2 => o1.service.id === o2.id));
				this.getDragDropForOrg(this.organizationServices);
			} catch (error) {
				console.error('Error during get services of user', error);
			} finally {
				this.isLoading = false;
			}
		}
	}

	drop(event: CdkDragDrop<DragDropServices[]>) {
		if (event.previousContainer === event.container) {
			moveItemInArray(
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		} else {
			transferArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		}
		this.sortAllSelectedService(this.addedServices);
		this.sortAllSelectedService(this.allServices);
	}

	onToggleChange() {
		this.typeOfTab.emit('Add all services');
		this.togglePositionChange.emit(true);
		this.addedServices.push(...this.allServices);
		this.allServices = [];
	}

	private sortAllSelectedService(serviceData: DragDropServices[]) {
		return serviceData.sort((a, b) => a.name.localeCompare(b.name));
	}
}
